import type { CSSProperties } from "react";
import { C, FU, T, W } from "./tokens";

/* Shared segmented control for the observatory shell — the one toggle idiom
   used across shell sections (1.1 metric switch, 1.4 ranking lens). Active state
   is a soft #82BAFF-tinted pill with blue text (the shell's identity accent),
   not a heavy navy fill — lighter, on-language, and consistent everywhere. */

export interface SegOption<T extends string> {
  key: T;
  label: string;
}

export function SegToggle<T extends string>({
  options,
  value,
  onChange,
  style,
  accent,
  elevated = false,
}: {
  options: readonly SegOption<T>[];
  value: T;
  onChange: (v: T) => void;
  style?: CSSProperties;
  /** Workspace identity hue for the active pill — defaults to the shell's blue.
   *  Prioritization passes its purple so the toggle matches its accent. */
  accent?: string;
  /** Command-bar variant: an opaque, higher-contrast track (always-opaque
   *  surface + strong border + a bolder active pill) so the control stays
   *  legible on the sticky observatory band, which is transparent at the top of
   *  the page. Card-embedded toggles leave this off. */
  elevated?: boolean;
}) {
  const hue = accent ?? C.accentItalic;
  return (
    <div
      style={{
        display: "inline-flex",
        gap: 2,
        padding: 3,
        borderRadius: 8,
        background: elevated ? C.tooltipBg : C.surface,
        border: `1px solid ${elevated ? C.borderStrong : C.border}`,
        ...style,
      }}
    >
      {options.map((o) => {
        const active = value === o.key;
        return (
          <button
            type="button"
            key={o.key}
            onClick={() => onChange(o.key)}
            style={{
              padding: "6px 13px",
              border: "none",
              borderRadius: 5,
              cursor: "pointer",
              fontFamily: FU,
              fontSize: T.cap,
              fontWeight: active ? W.semibold : W.medium,
              background: active ? `color-mix(in srgb, ${hue} ${elevated ? 22 : 16}%, transparent)` : "transparent",
              color: active ? hue : C.textMd,
              transition: "background 150ms ease, color 150ms ease",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
